import { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Button } from '../ui/button';
import { UPDATE_BULLETIN_POST } from '../../graphql/bulletinBoard';
import { LIST_STAFF, LIST_CLIENTS, GET_CURRENT_USER } from '../../graphql/queries';
import MDEditor from '@uiw/react-md-editor';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { uploadBulletinAttachment, AttachmentMetadata } from '../../utils/attachmentUpload';

interface PostImage {
  id: string;
  url: string;
  thumbnailUrl?: string;
  width?: number;
  height?: number;
  size?: number;
}

interface PostAttachment {
  id: string;
  url: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedAt: string;
  isImage: boolean;
}

interface LocalAttachment {
  id: string;
  file?: File;
  fileName: string;
  fileType: string;
  fileSize: number;
  isImage: boolean;
  previewUrl?: string;
  uploadProgress?: number;
  uploadError?: string;
  metadata?: AttachmentMetadata;
  isExisting?: boolean; // Flag to track if this is an existing attachment
}

interface PostReactions {
  thumbsUp: string[];
  heart: string[];
  party: string[];
  eyes: string[];
}

interface BulletinPost {
  id: string;
  content: string;
  audienceType: string;
  creatorId: string;
  creator: {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
  targetedUserIds?: string[];
  priority: string;
  isPinned: boolean;
  images: PostImage[];
  attachments?: PostAttachment[];
  reactions: PostReactions;
}

interface EditPostModalProps {
  post: BulletinPost;
  isOpen: boolean;
  onClose: () => void;
  onPostUpdated: () => void;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

export function EditPostModal({ post, isOpen, onClose, onPostUpdated }: EditPostModalProps) {
  const [content, setContent] = useState(post.content);
  const [audienceType, setAudienceType] = useState(post.audienceType);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(post.targetedUserIds || []);
  const [searchTerm, setSearchTerm] = useState('');
  const [validationError, setValidationError] = useState('');
  const [priority, setPriority] = useState(post.priority || 'NORMAL');
  const [isPinned, setIsPinned] = useState(post.isPinned || false);
  const [images, setImages] = useState<PostImage[]>(post.images || []);
  const [showPreview, setShowPreview] = useState(false);
  const [attachments, setAttachments] = useState<LocalAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showImageInsertPopover, setShowImageInsertPopover] = useState(false);
  const [selectedImageForInsert, setSelectedImageForInsert] = useState<LocalAttachment | null>(null);
  const [imageAltText, setImageAltText] = useState('');
  const imageFileInputRef = useRef<HTMLInputElement>(null);
  const documentFileInputRef = useRef<HTMLInputElement>(null);

  const { data: staffData } = useQuery<{ listStaff: User[] }>(LIST_STAFF);
  const { data: clientsData } = useQuery<{ listClients: User[] }>(LIST_CLIENTS);
  const { data: userData } = useQuery<{ getCurrentUser: any }>(GET_CURRENT_USER);

  const [updatePost, { loading }] = useMutation<{ updateBulletinPost: any }>(UPDATE_BULLETIN_POST);

  const user = userData?.getCurrentUser;
  const isAdmin = user?.role === 'ADMIN';
  const isCreator = user?.id === post.creatorId;

  useEffect(() => {
    // Reset form when post changes
    setContent(post.content);
    setAudienceType(post.audienceType);
    setSelectedUserIds(post.targetedUserIds || []);
    setPriority(post.priority || 'NORMAL');
    setIsPinned(post.isPinned || false);
    setImages(post.images || []);
    
    // Load existing attachments
    if (post.attachments && post.attachments.length > 0) {
      const existingAttachments: LocalAttachment[] = post.attachments.map((att) => ({
        id: att.id,
        fileName: att.fileName,
        fileType: att.fileType,
        fileSize: att.fileSize,
        isImage: att.isImage,
        isExisting: true,
        metadata: {
          id: att.id,
          url: att.url,
          fileName: att.fileName,
          fileType: att.fileType,
          fileSize: att.fileSize,
          isImage: att.isImage,
        },
      }));
      setAttachments(existingAttachments);
    } else {
      setAttachments([]);
    }
  }, [post]);

  const onDrop = (acceptedFiles: File[]) => {
    acceptedFiles.forEach((file) => {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
        return;
      }

      // Validate file format
      const validFormats = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validFormats.includes(file.type)) {
        toast.error(`${file.name} is not a supported format (JPEG, PNG, GIF, WebP)`);
        return;
      }

      // Create preview URL
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const newImage: PostImage = {
            id: `temp-${Date.now()}-${Math.random()}`,
            url: reader.result as string,
            thumbnailUrl: reader.result as string,
            width: img.width,
            height: img.height,
            size: file.size,
          };
          setImages((prev) => [...prev, newImage]);
          toast.success(`${file.name} added`);
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
    },
    multiple: true,
  });

  // Handle file selection for images
  const handleImageFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach((file) => {
      // Check max attachments limit
      if (attachments.length >= 10) {
        toast.error('Maximum 10 attachments per post');
        return;
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
        return;
      }

      // Validate file format
      const validFormats = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validFormats.includes(file.type)) {
        toast.error(`${file.name} is not a supported format (JPEG, PNG, GIF, WebP)`);
        return;
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);

      const newAttachment: LocalAttachment = {
        id: `temp-${Date.now()}-${Math.random()}`,
        file,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        isImage: true,
        previewUrl,
        isExisting: false,
      };

      setAttachments((prev) => [...prev, newAttachment]);
    });

    // Reset input
    if (imageFileInputRef.current) {
      imageFileInputRef.current.value = '';
    }
  };

  // Handle file selection for documents
  const handleDocumentFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach((file) => {
      // Check max attachments limit
      if (attachments.length >= 10) {
        toast.error('Maximum 10 attachments per post');
        return;
      }

      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
        return;
      }

      // Validate file format
      const validFormats = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
      ];
      if (!validFormats.includes(file.type)) {
        toast.error(`${file.name} is not a supported format (PDF, DOC, DOCX, XLS, XLSX, TXT)`);
        return;
      }

      const newAttachment: LocalAttachment = {
        id: `temp-${Date.now()}-${Math.random()}`,
        file,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        isImage: false,
        isExisting: false,
      };

      setAttachments((prev) => [...prev, newAttachment]);
    });

    // Reset input
    if (documentFileInputRef.current) {
      documentFileInputRef.current.value = '';
    }
  };

  // Remove attachment
  const removeAttachment = (id: string) => {
    const attachment = attachments.find((att) => att.id === id);
    if (attachment?.previewUrl) {
      URL.revokeObjectURL(attachment.previewUrl);
    }
    setAttachments((prev) => prev.filter((att) => att.id !== id));
  };

  // Open image insert popover
  const openImageInsertPopover = (attachment: LocalAttachment) => {
    if (!attachment.metadata) {
      toast.error('Image must be uploaded before inserting');
      return;
    }
    setSelectedImageForInsert(attachment);
    setImageAltText('');
    setShowImageInsertPopover(true);
  };

  // Insert image into content
  const insertImageIntoContent = () => {
    if (!selectedImageForInsert?.metadata) {
      toast.error('Image must be uploaded before inserting');
      return;
    }

    const altText = imageAltText.trim() || 'Image';
    const markdownImage = `![${altText}](${selectedImageForInsert.metadata.url})`;

    setContent((prev) => prev + (prev ? '\n\n' : '') + markdownImage);
    setShowImageInsertPopover(false);
    setSelectedImageForInsert(null);
    setImageAltText('');
    toast.success('Image inserted into post');
  };

  // Upload new attachments (skip existing ones)
  const uploadAttachments = async (): Promise<AttachmentMetadata[]> => {
    const uploadedMetadata: AttachmentMetadata[] = [];

    for (let i = 0; i < attachments.length; i++) {
      const attachment = attachments[i];

      // Skip if already uploaded (existing attachment)
      if (attachment.metadata && attachment.isExisting) {
        uploadedMetadata.push(attachment.metadata);
        continue;
      }

      // Skip if already uploaded (new attachment that was uploaded)
      if (attachment.metadata && !attachment.isExisting) {
        uploadedMetadata.push(attachment.metadata);
        continue;
      }

      if (!attachment.file) {
        throw new Error(`File missing for attachment ${attachment.fileName}`);
      }

      try {
        // Update progress
        setAttachments((prev) =>
          prev.map((att, idx) =>
            idx === i ? { ...att, uploadProgress: 0, uploadError: undefined } : att
          )
        );

        const metadata = await uploadBulletinAttachment({
          file: attachment.file,
          onProgress: (progress) => {
            setAttachments((prev) =>
              prev.map((att, idx) => (idx === i ? { ...att, uploadProgress: progress } : att))
            );
          },
        });

        // Store metadata
        setAttachments((prev) =>
          prev.map((att, idx) => (idx === i ? { ...att, metadata, uploadProgress: 100 } : att))
        );

        uploadedMetadata.push(metadata);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        setAttachments((prev) =>
          prev.map((att, idx) =>
            idx === i ? { ...att, uploadError: errorMessage, uploadProgress: undefined } : att
          )
        );
        throw new Error(`Failed to upload ${attachment.fileName}: ${errorMessage}`);
      }
    }

    return uploadedMetadata;
  };

  // Get file icon based on file type
  const getFileIcon = (fileType: string): string => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('text')) return '📃';
    return '📎';
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!isOpen) return null;

  const isSpecificAudience = audienceType === 'SPECIFIC_STAFF' || audienceType === 'SPECIFIC_CLIENTS';
  const availableUsers: User[] =
    audienceType === 'SPECIFIC_STAFF'
      ? staffData?.listStaff || []
      : audienceType === 'SPECIFIC_CLIENTS'
      ? clientsData?.listClients || []
      : [];

  const filteredUsers = availableUsers.filter(
    (user) =>
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleUserToggle = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const validateInput = (): boolean => {
    setValidationError('');

    if (!content.trim()) {
      setValidationError('Post content cannot be empty');
      return false;
    }

    if (content.length > 5000) {
      setValidationError('Post content cannot exceed 5000 characters');
      return false;
    }

    if (isSpecificAudience && selectedUserIds.length === 0) {
      setValidationError('Please select at least one user for specific audience types');
      return false;
    }

    // Check for upload errors
    const hasUploadErrors = attachments.some((att) => att.uploadError);
    if (hasUploadErrors) {
      setValidationError('Some attachments failed to upload. Please remove or retry.');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateInput()) return;

    setIsUploading(true);

    try {
      // Upload all new attachments first
      let uploadedAttachments: AttachmentMetadata[] = [];
      if (attachments.length > 0) {
        uploadedAttachments = await uploadAttachments();
      }

      const input: any = {
        content: content.trim(),
        audienceType,
        targetedUserIds: isSpecificAudience ? selectedUserIds : undefined,
        priority,
        isPinned,
      };

      // Add attachments to input
      if (uploadedAttachments.length > 0) {
        input.attachments = uploadedAttachments.map((att) => ({
          id: att.id,
          url: att.url,
          fileName: att.fileName,
          fileType: att.fileType,
          fileSize: att.fileSize,
          isImage: att.isImage,
        }));
      }

      // Keep images for backward compatibility (if any were added via drag-drop)
      if (images.length > 0) {
        input.images = images;
      }

      await updatePost({
        variables: {
          id: post.id,
          input,
        },
      });

      // Clean up preview URLs
      attachments.forEach((att) => {
        if (att.previewUrl && !att.isExisting) {
          URL.revokeObjectURL(att.previewUrl);
        }
      });

      setValidationError('');
      setIsUploading(false);
      toast.success('Post updated successfully!');
      onPostUpdated();
    } catch (error: any) {
      console.error('Error updating post:', error);
      setValidationError(error.message || 'Failed to update post');
      toast.error(error.message || 'Failed to update post');
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    // Clean up preview URLs for new attachments
    attachments.forEach((att) => {
      if (att.previewUrl && !att.isExisting) {
        URL.revokeObjectURL(att.previewUrl);
      }
    });

    // Reset to original values
    setContent(post.content);
    setAudienceType(post.audienceType);
    setSelectedUserIds(post.targetedUserIds || []);
    setSearchTerm('');
    setValidationError('');
    setPriority(post.priority || 'NORMAL');
    setIsPinned(post.isPinned || false);
    setImages(post.images || []);
    setShowPreview(false);
    setIsUploading(false);
    setAttachments([]);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-aalaw-charcoal">Edit Bulletin Post</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-4">
            {/* Content Input with Markdown Editor */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Post Content <span className="text-red-500">*</span>
                </label>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="text-xs text-aalaw-red hover:text-aalaw-red-dark"
                >
                  {showPreview ? 'Hide Preview' : 'Show Preview'}
                </button>
              </div>
              <div data-color-mode="light">
                <MDEditor
                  value={content}
                  onChange={(val) => setContent(val || '')}
                  preview={showPreview ? 'preview' : 'edit'}
                  height={300}
                  visibleDragbar={false}
                />
              </div>
              <div className="flex justify-between items-center mt-1">
                <p className="text-xs text-gray-500">Supports markdown formatting</p>
                <p className={`text-xs ${content.length > 5000 ? 'text-red-500' : 'text-gray-500'}`}>
                  {content.length} / 5000
                </p>
              </div>
            </div>

            {/* Priority and Pin Row */}
            <div className="grid grid-cols-2 gap-4">
              {/* Priority Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority Level
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                  disabled={loading}
                >
                  <option value="NORMAL">Normal</option>
                  <option value="IMPORTANT">⚠️ Important</option>
                  <option value="URGENT">🚨 Urgent</option>
                </select>
              </div>

              {/* Pin Toggle */}
              {(isAdmin || isCreator) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pin Post
                  </label>
                  <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={isPinned}
                      onChange={(e) => setIsPinned(e.target.checked)}
                      className="rounded"
                      disabled={loading}
                    />
                    <span className="text-sm text-gray-700">Pin to top of feed</span>
                  </label>
                </div>
              )}
            </div>

            {/* Attachments Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attachments ({attachments.length}/10)
              </label>

              {/* Attachment Buttons */}
              <div className="flex gap-3 mb-4">
                <input
                  ref={imageFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  multiple
                  onChange={handleImageFileSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => imageFileInputRef.current?.click()}
                  disabled={attachments.length >= 10 || loading || isUploading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-aalaw-red rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm font-semibold text-aalaw-red">Upload Image</span>
                </button>

                <input
                  ref={documentFileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain"
                  multiple
                  onChange={handleDocumentFileSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => documentFileInputRef.current?.click()}
                  disabled={attachments.length >= 10 || loading || isUploading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-aalaw-red rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-sm font-semibold text-aalaw-red">Upload File</span>
                </button>
              </div>

              {/* Attachment List */}
              {attachments.length > 0 && (
                <div className="space-y-3">
                  {attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg"
                    >
                      {/* Thumbnail or Icon */}
                      {attachment.isImage && (attachment.previewUrl || attachment.metadata?.url) ? (
                        <img
                          src={attachment.previewUrl || attachment.metadata?.url}
                          alt={attachment.fileName}
                          className="w-16 h-16 object-cover rounded"
                        />
                      ) : (
                        <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded text-2xl">
                          {getFileIcon(attachment.fileType)}
                        </div>
                      )}

                      {/* File Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {attachment.fileName}
                        </p>
                        <p className="text-xs text-gray-500">{formatFileSize(attachment.fileSize)}</p>

                        {/* Upload Progress */}
                        {attachment.uploadProgress !== undefined && attachment.uploadProgress < 100 && (
                          <div className="mt-2">
                            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-aalaw-red transition-all duration-300"
                                style={{ width: `${attachment.uploadProgress}%` }}
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Uploading... {attachment.uploadProgress}%
                            </p>
                          </div>
                        )}

                        {/* Upload Error */}
                        {attachment.uploadError && (
                          <p className="text-xs text-red-600 mt-1">{attachment.uploadError}</p>
                        )}

                        {/* Success Indicator */}
                        {attachment.metadata && !attachment.uploadProgress && (
                          <div className="flex items-center gap-1 mt-1">
                            <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-xs text-green-600">
                              {attachment.isExisting ? 'Existing' : 'Uploaded'}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {/* Insert Button for Images */}
                        {attachment.isImage && attachment.metadata && (
                          <button
                            type="button"
                            onClick={() => openImageInsertPopover(attachment)}
                            className="p-2 text-aalaw-red hover:bg-red-50 rounded transition-colors"
                            title="Insert into post"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        )}

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={() => removeAttachment(attachment.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Remove"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Audience Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Audience <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="ALL_USERS"
                    checked={audienceType === 'ALL_USERS'}
                    onChange={(e) => {
                      setAudienceType(e.target.value);
                      setSelectedUserIds([]);
                    }}
                    className="mr-2"
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-700">All Users (Staff & Clients)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="ALL_STAFF"
                    checked={audienceType === 'ALL_STAFF'}
                    onChange={(e) => {
                      setAudienceType(e.target.value);
                      setSelectedUserIds([]);
                    }}
                    className="mr-2"
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-700">All Staff</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="ALL_CLIENTS"
                    checked={audienceType === 'ALL_CLIENTS'}
                    onChange={(e) => {
                      setAudienceType(e.target.value);
                      setSelectedUserIds([]);
                    }}
                    className="mr-2"
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-700">All Clients</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="SPECIFIC_STAFF"
                    checked={audienceType === 'SPECIFIC_STAFF'}
                    onChange={(e) => {
                      setAudienceType(e.target.value);
                      setSelectedUserIds([]);
                    }}
                    className="mr-2"
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-700">Specific Staff Members</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="SPECIFIC_CLIENTS"
                    checked={audienceType === 'SPECIFIC_CLIENTS'}
                    onChange={(e) => {
                      setAudienceType(e.target.value);
                      setSelectedUserIds([]);
                    }}
                    className="mr-2"
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-700">Specific Clients</span>
                </label>
              </div>
            </div>

            {/* User Selection for Specific Audiences */}
            {isSpecificAudience && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Users <span className="text-red-500">*</span>
                </label>

                {/* Search Input */}
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent mb-2"
                  disabled={loading}
                />

                {/* User List */}
                <div className="border border-gray-300 rounded-lg max-h-60 overflow-y-auto">
                  {filteredUsers.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      No users found
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {filteredUsers.map((user) => (
                        <label
                          key={user.id}
                          className="flex items-center p-3 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(user.id)}
                            onChange={() => handleUserToggle(user.id)}
                            className="mr-3"
                            disabled={loading}
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {user.firstName} {user.lastName}
                            </p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {selectedUserIds.length > 0 && (
                  <p className="text-xs text-gray-600 mt-2">
                    {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>
            )}

            {/* Validation Error */}
            {validationError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{validationError}</p>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <Button variant="outline" onClick={handleClose} disabled={loading || isUploading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || isUploading}
            className="bg-aalaw-red hover:bg-aalaw-red-dark text-white"
          >
            {loading || isUploading ? 'Updating...' : 'Update Post'}
          </Button>
        </div>
      </div>

      {/* Image Insert Popover */}
      {showImageInsertPopover && selectedImageForInsert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Insert Image</h4>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Alt Text (optional)
              </label>
              <input
                type="text"
                value={imageAltText}
                onChange={(e) => setImageAltText(e.target.value)}
                placeholder="Describe the image..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
                autoFocus
              />
            </div>

            <div className="mb-6 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs font-medium text-gray-600 mb-1">Preview:</p>
              <code className="text-xs text-gray-800 break-all">
                ![{imageAltText || 'Image'}]({selectedImageForInsert.metadata?.url || ''})
              </code>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowImageInsertPopover(false);
                  setSelectedImageForInsert(null);
                  setImageAltText('');
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={insertImageIntoContent}
                className="flex-1 bg-aalaw-red hover:bg-aalaw-red-dark text-white"
              >
                Insert
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
